import type { StaticEntityType } from "@config";
import {
  createContext,
  useContext,
  type ComponentType,
  type ReactNode,
} from "react";
import type {
  ITableCellDropTargetProps,
  ITableCellStackProps,
  ITableEditorData,
  ITableSettings,
} from "../types";
import { ensureTableElementSettings } from "../utils/ensure-table-element-settings";
import type { ColumnFilter, TableFilterState } from "../utils/apply-filters";
import type { ColumnKind } from "../utils/derive-column-kind";

export type TableSortState = { col: number; direction: "asc" | "desc" } | null;

export interface ITableCtx {
  entityId: string;
  isMobile: boolean;
  editorData?: ITableEditorData;
  elementSettings: ITableSettings;
  rowOrder?: number[];
  sortState?: TableSortState;
  headerMenuCol?: number | null;
  onHeaderTriggerClick?: (col: number) => void;
  onSortApply?: (col: number, direction: "asc" | "desc" | null) => void;
  filterState?: TableFilterState;
  openFilterCol?: number | null;
  columnKindByCol?: ColumnKind[];
  onFilterTriggerClick?: (col: number) => void;
  onFilterChange?: (col: number, next: ColumnFilter | null) => void;
  // Editor-only, version-mode resolver that turns `{{var}}` template tokens in
  // a cell's content into resolved variable pills. Supplied by the (store-
  // connected) TableEditor; absent in the published viewer, which receives
  // already-resolved content. Applied only to the inactive cell render so the
  // active Froala editor and the save path keep raw templates.
  resolveCellContent?: (content: string) => string;
  // Static render path for in-cell elements: the table entity's nested
  // children grouped by settings.cellId (published/preview JSON). The editor
  // renders live children via InCellStack instead.
  cellChildren?: Record<string, StaticEntityType[]>;
  // Live (store-backed) cell → child ids. Outside editorData so a cell knows
  // its stack renders even when the table is inactive.
  cellChildIds?: Record<string, string[]>;
  // Editor-injected live in-cell element stack renderer. Present in editor
  // contexts regardless of table-active state (the stack must render for
  // inactive tables too); absent in the published viewer.
  InCellStack?: ComponentType<ITableCellStackProps>;
  // Like InCellStack: outside editorData so inactive tables stay droppable.
  CellDropTarget?: ComponentType<ITableCellDropTargetProps>;
}

const initState: ITableCtx = {
  entityId: "",
  isMobile: false,
  elementSettings: ensureTableElementSettings(),
};

export const TableCtx = createContext<ITableCtx>(initState);

interface IProps {
  children: ReactNode;
  ctxState: ITableCtx;
}

export const TableCtxProvider = ({ children, ctxState }: IProps) => {
  return <TableCtx.Provider value={ctxState}>{children}</TableCtx.Provider>;
};

export const useTableCtx = () => {
  const ctx = useContext(TableCtx);
  if (!ctx) throw Error("useTableCtx: TableCtx is not defined!");

  return ctx;
};
